/**
 * Tab switching for the category-grouped color picker (snippets/color-group-picker.liquid).
 *
 * Switching a tab reveals that group's swatches and selects the first variant in the group so
 * the theme's <variant-picker> updates price, gallery and ATC. The active tab is still resolved
 * from the selected option value after each re-render.
 */
class ColorGroupPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  get tabs() {
    return Array.from(this.querySelectorAll('[data-color-group-tab]'));
  }

  /**
   * Reveal the panel for the given group and mark its tab as active.
   *
   * @param {string} group - the group handle, matching `data-color-group-tab`
   * @param {boolean} [focusTab] - move focus to the newly activated tab
   */
  activate(group, focusTab = false) {
    const previousGroup = this.getAttribute('active-group');

    for (const tab of this.tabs) {
      const isActive = tab.getAttribute('data-color-group-tab') === group;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;

      if (isActive && focusTab) {
        tab.focus();
      }
    }

    for (const panel of this.querySelectorAll('[data-color-group-panel]')) {
      panel.hidden = panel.getAttribute('data-color-group-panel') !== group;
    }

    this.setAttribute('active-group', group);

    if (previousGroup !== group) {
      this.#selectFirstVariant(group);
    }
  }

  /**
   * Check the first visible swatch in the group so <variant-picker> treats it as a normal option change.
   *
   * @param {string} group - the group handle, matching `data-color-group-panel`
   */
  #selectFirstVariant(group) {
    const panel = this.querySelector(`[data-color-group-panel="${CSS.escape(group)}"]`);

    if (!panel) return;

    const firstItem = Array.from(panel.querySelectorAll('.color-group-picker__item')).find((item) => !item.hidden);

    if (!firstItem) return;

    const input = firstItem.querySelector('input[data-option-position]');

    if (input && !input.disabled) {
      if (input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    firstItem.querySelector('a.color-group-picker__value')?.click();
  }

  #onClick = (event) => {
    const tab = event.target.closest('[data-color-group-tab]');

    if (!tab || !this.contains(tab)) return;

    this.activate(tab.getAttribute('data-color-group-tab'));
  };

  #onKeydown = (event) => {
    const tab = event.target.closest('[data-color-group-tab]');

    if (!tab || !this.contains(tab)) return;

    const offsets = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 };
    const offset = offsets[event.key];

    if (offset === undefined) return;

    const { tabs } = this;
    const next = tabs[(tabs.indexOf(tab) + offset + tabs.length) % tabs.length];

    if (!next) return;

    event.preventDefault();
    this.activate(next.getAttribute('data-color-group-tab'), true);
  };
}

if (!window.customElements.get('color-group-picker')) {
  window.customElements.define('color-group-picker', ColorGroupPicker);
}
